import React, {
  useState,
  useRef,
  useContext,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import axios from "axios";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import TimePicker from "react-time-picker";
import "react-time-picker/dist/TimePicker.css";
import { OperaContext } from "../../../../context/OperaContext";
import CastTable from "./CastTable";
import CrewTable from "./CrewTable";
import { inputClass } from "./constants";
import { toast } from "react-toastify";
import ImageModal from "../../../../components/ImagePreviewModal";
import useGoogleDriveImagePicker from "../../../../components/useGoogleDriveImagePicker";
import ImagePreview from "../../../../components/imagePreview";
import Collapsible from "../../../../components/Collapsible";
import ImageCropModal from "./image-crop-modal";

function EventForm({
  editingEvent,
  setEditingEvent,
  prePopulateData,
  isSectionOpen,
  setIsSectionOpen,
}) {
  const {
    venues,
    setUnpublishedEvents,
    fetchAdminData,
    setPrePopulateEventData,
    eventFormData,
    setEventFormData,
  } = useContext(OperaContext);

  const initialFormData = React.useMemo(
    () => ({
      title: "",
      venue: "",
      city: "",
      genre: "",
      includedInPrice: "",
      optionalExtras: "",
      composers: [],
      date_times: [],
      date_time: [],
      defaultTime: "19:00",
      image: null,
      cast: [],
      crews: [],
      basePrice: 0,
    }),
    []
  );

  const [formData, setFormData] = useState(initialFormData);
  const [isFormInitialized, setIsFormInitialized] = useState(false);
  const [nonFamousPerformer, setNonFamousPerformer] = useState({
    name: "",
    role: "",
  });
  const [famousPerformerInput, setFamousPerformerInput] = useState({
    id: "",
    role: "",
  });
  const [newCrewMember, setNewCrewMember] = useState({ name: "", role: "" });
  const [composerId, setComposerId] = useState("");
  const [newComposerName, setNewComposerName] = useState("");
  const [errors, setErrors] = useState({});
  const [allFamousPerformers, setAllFamousPerformers] = useState([]);
  const [allComposers, setAllComposers] = useState([]);
  const [venueDefaultHotels, setVenueDefaultHotels] = useState({});
  const [hotelNames, setHotelNames] = useState({});
  const flatpickrRef = useRef(null);
  const fileInputRef = useRef(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalImage, setModalImage] = useState("");
  const [publishEvents, setPublishEvents] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [isCropOpen, setIsCropOpen] = useState(false);
  const [originalFile, setOriginalFile] = useState(null);


  const openModal = (image) => {
    setModalImage(image);
    setModalOpen(true);
  };

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  // Helper function to safely parse date/time data
  const parseDateTime = useCallback((dateTimeArray) => {
    if (!Array.isArray(dateTimeArray) || dateTimeArray.length === 0) return [];

    return dateTimeArray.map((dt) => {
      try {
        let dateStr = dt.date;
        let time = dt.time ?? null;

        if (typeof dt.date === "string") {
          // Check if dt.date contains time (ISO timestamp)
          if (dt.date.includes("T")) {
            // Parse ISO timestamp and extract hours/minutes if time not explicitly set
            const parsedDate = new Date(dt.date);
            if (!isNaN(parsedDate.getTime()) && !dt.time) {
              const hours = parsedDate.getUTCHours().toString().padStart(2, "0");
              const minutes = parsedDate.getUTCMinutes().toString().padStart(2, "0");
              time = `${hours}:${minutes}`;
            }
          } else {
            // Date-only string → keep time null
            time = null;
          }
        }

        // Validate date
        const parsedDateCheck = new Date(dt.date);
        if (isNaN(parsedDateCheck.getTime())) {
          const now = new Date();
          dateStr = now.toISOString().split("T")[0];
          time = null;
        }

        return {
          date: dateStr,
          time,
          cost: dt.cost ?? "0",
        };
      } catch (error) {
        console.error("Error parsing date/time:", error, dt);
        return {
          date: new Date().toISOString().split("T")[0],
          time: null,
          cost: dt.cost ?? "0",
        };
      }
    });
  }, []);



  // Helper function to safely extract composer IDs
  const extractComposerIds = useCallback((composers) => {
    if (!Array.isArray(composers)) return [];

    return composers
      .map((composer) => {
        if (typeof composer === "string") {
          return composer; // Already an ID
        } else if (composer && composer._id) {
          return composer._id; // Extract ID from object
        } else {
          console.warn("Invalid composer format:", composer);
          return null;
        }
      })
      .filter(Boolean); // Remove null values
  }, []);

  // Consolidated form data initialization
  const initializeFormData = useCallback(
    (sourceData) => {
      if (!sourceData) return initialFormData;

      const parsedDateTimes = parseDateTime(sourceData.date_times || []);
      const composerIds = extractComposerIds(sourceData.composers || []);

      return {
        title: sourceData.title || "",
        venue: sourceData.venue?._id || sourceData.venue || "",
        city: sourceData.venue?.city || sourceData.city || "",
        genre: sourceData.genre || "",
        includedInPrice: sourceData.includedInPrice || "",
        optionalExtras: sourceData.optionalExtras || "",
        composers: composerIds,
        date_times: parsedDateTimes,
        defaultTime: "19:00",
        image: sourceData?.image,
        cast: Array.isArray(sourceData.cast) ? [...sourceData.cast] : [],
        crews: Array.isArray(sourceData.crews) ? [...sourceData.crews] : [],
        basePrice: parseFloat(sourceData.basePrice) || 0,
      };
    },
    [initialFormData, parseDateTime, extractComposerIds]
  );

  // Fetch hotel names to display instead of IDs
  const fetchHotelNames = useCallback(async () => {
    try {
      const response = await axios.get("/api/hotels", {
        timeout: 15000,
        withCredentials: true,
      });

      // Create a mapping of hotel IDs to names
      const hotelMap = {};
      response.data.hotels.forEach((hotel) => {
        hotelMap[hotel._id] = hotel.name;
      });

      //console.log('Hotel names mapping:', hotelMap);
      setHotelNames(hotelMap);
    } catch (error) {
      console.error("Error fetching hotel names:", error.message);
    }
  }, []);

  const fetchFormData = useCallback(async (retryCount = 0, maxRetries = 3) => {
    try {
      //console.log('Fetching form data for event form...');
      // Use withCredentials to ensure auth cookies are sent
      // Handle each request separately to better identify which one is failing
      let performersRes, composersRes, venuesRes;

      try {
        performersRes = await axios.get("/api/famous-performers/all", {
          timeout: 15000,
          withCredentials: true,
        });
        //console.log('Successfully fetched performers:', performersRes.data.length);
      } catch (error) {
        console.error("Error fetching performers:", error.message);
        throw new Error(`Failed to fetch performers: ${error.message}`);
      }

      try {
        composersRes = await axios.get("/api/composers/all", {
          timeout: 15000,
          withCredentials: true,
        });
        //console.log('Successfully fetched composers:', composersRes.data.length);
      } catch (error) {
        console.error("Error fetching composers:", error.message);
        throw new Error(`Failed to fetch composers: ${error.message}`);
      }

      try {
        // Try the admin endpoint first, fall back to regular endpoint if it fails
        try {
          venuesRes = await axios.get("/api/venues/admin/all", {
            timeout: 15000,
            withCredentials: true,
          });
        } catch (adminError) {
          console.warn(
            "Admin venues endpoint failed, trying public endpoint:",
            adminError.message
          );
          venuesRes = await axios.get("/api/venues", {
            timeout: 15000,
            withCredentials: true,
          });
        }
        //console.log('Successfully fetched venues:', venuesRes.data.length);
      } catch (error) {
        console.error("Error fetching venues:", error.message);
        throw new Error(`Failed to fetch venues: ${error.message}`);
      }

      // Sort famous performers alphabetically by name
      const sortedPerformers = [...performersRes.data].sort((a, b) =>
        a.name.localeCompare(b.name)
      );

      // Sort composers alphabetically by name
      const sortedComposers = [...composersRes.data].sort((a, b) =>
        a.name.localeCompare(b.name)
      );

      setAllFamousPerformers(sortedPerformers);
      setAllComposers(sortedComposers);

      // Create a mapping of venue IDs to their default hotel info
      const defaultHotelsMap = {};
      //console.log('Venues data for default hotel mapping:', venuesRes.data);
      venuesRes.data.forEach((venue) => {
        //console.log(`Checking venue ${venue.name} (${venue._id}) for defaultHotelRoom:`, venue.defaultHotelRoom);
        if (venue.defaultHotelRoom && venue.defaultHotelRoom.hotelId) {
          defaultHotelsMap[venue._id] = venue.defaultHotelRoom;
          //console.log(`Added default hotel for venue ${venue.name}:`, venue.defaultHotelRoom);
        }
      });
      //console.log('Final defaultHotelsMap:', defaultHotelsMap);
      setVenueDefaultHotels(defaultHotelsMap);

      setErrors((prev) => ({ ...prev, form: "" }));
    } catch (err) {
      console.error("Error fetching form data:", {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data,
        attempt: retryCount + 1,
      });
      if (retryCount < maxRetries) {
        const delay = 3000 * Math.pow(2, retryCount);
        //console.log(`Retrying form data fetch in ${delay}ms (attempt ${retryCount + 2}/${maxRetries + 1})`);
        await sleep(delay);
        return fetchFormData(retryCount + 1, maxRetries);
      } else {
        setErrors((prev) => ({
          ...prev,
          form: `Failed to load data: ${err.message}`,
        }));
      }
    }
  }, []);

  const submitEvent = React.useCallback(
    async (data, isEdit = false, id = null) => {
      try {
        const url = isEdit ? `/api/events/${id}` : "/api/events";
        const method = isEdit ? "put" : "post";
        const token = localStorage.getItem("token");
        const res = await axios({
          method,
          url,
          data,
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: token ? `Bearer ${token}` : "",
          },
          timeout: 15000,
          withCredentials: true,
        });

        if (!isEdit) setUnpublishedEvents((prev) => [...prev, res.data]);
        await fetchAdminData();
        setErrors((prev) => ({ ...prev, form: "" }));
        return res;
      } catch (err) {
        const errorMessage =
          err.response?.data?.error ||
          err.response?.data?.message ||
          err.message;
        toast.error(errorMessage);
      }
    },
    [setUnpublishedEvents, fetchAdminData, setErrors, sleep]
  );

  const addFamousPerformer = async (
    performer,
    retryCount = 0,
    maxRetries = 3
  ) => {
    try {
      const existingPerformer = allFamousPerformers.find(
        (p) => p.name.toLowerCase() === performer.name.toLowerCase()
      );
      if (existingPerformer) {
        throw new Error("Performer already exists in Famous Performers");
      }

      const formData = new FormData();
      formData.append("name", performer.name);
      formData.append("aliases", JSON.stringify([performer.name]));
      formData.append("description", "");
      formData.append("published", true);
      formData.append("isImported", true);
      const res = await axios.post("/api/famous-performers", formData, {
        timeout: 15000,
      });
      //console.log('Added famous performer:', res.data);
      setAllFamousPerformers((prev) => [...prev, res.data]);
      setFormData((prev) => ({
        ...prev,
        cast: prev.cast.map((p) =>
          p.name.toLowerCase() === performer.name.toLowerCase()
            ? { ...p, famousPerformer: res.data._id }
            : p
        ),
      }));
      setErrors((prev) => ({ ...prev, performer: "" }));
      return res.data;
    } catch (err) {
      console.error("Error adding famous performer:", {
        message: err.message,
        attempt: retryCount + 1,
      });
      if (retryCount < maxRetries) {
        const delay = 3000 * Math.pow(2, retryCount);
        //console.log(`Retrying add famous performer in ${delay}ms (attempt ${retryCount + 2}/${maxRetries + 1})`);
        await sleep(delay);
        return addFamousPerformer(performer, retryCount + 1, maxRetries);
      } else {
        throw new Error(err.message || "Failed to add to Famous Performers");
      }
    }
  };

  const addComposer = useCallback(async (name) => {
    try {
      const formData = new FormData();
      formData.append("name", name);
      formData.append("description", "");

      const res = await axios.post("/api/composers", formData, {
        timeout: 15000,
      });

      if (res.status === 201) {
        toast.success("Composer added successfully");
      }

      setAllComposers((prev) => [...prev, res.data]);
      setErrors((prev) => ({ ...prev, newComposer: "" }));
      return res.data;
    } catch (err) {
      const errorMessage =
        err?.response?.data?.message || "Failed to add new composer";
      toast.error(errorMessage);
      throw new Error(errorMessage);
    }
  });
  const handlePublish = async (id) => {
    const response = await axios.put(
      `/api/events/${id}/publish`,
      {},
      { timeout: 15000, withCredentials: true }
    );
    return response;
  };

  useEffect(() => {
    fetchFormData();
    fetchHotelNames();
  }, [fetchFormData, fetchHotelNames]);

  // Consolidated form initialization effect
  useEffect(() => {
    // Wait for composers to be loaded before initializing form
    if (allComposers.length === 0) return;

    let newFormData;
    if (editingEvent) {
      // Initialize with editing event data
      newFormData = initializeFormData(editingEvent);
      setIsFormInitialized(true);
      setIsSectionOpen(true);
    } else if (eventFormData && !isFormInitialized) {
      // Initialize with context event form data
      newFormData = initializeFormData(eventFormData);
      setIsFormInitialized(true);
    } else if (!editingEvent && !eventFormData) {
      // Reset to initial form data
      newFormData = initialFormData;
      setIsFormInitialized(false);
    }

    if (newFormData) {
      setFormData(newFormData);
      // Sync with context
      if (!editingEvent) {
        setEventFormData(newFormData);
      }
    }
  }, [
    editingEvent,
    eventFormData,
    allComposers,
    initializeFormData,
    initialFormData,
    isFormInitialized,
    setEventFormData,
  ]);

  // Handle pre-population from venue selection
  useEffect(() => {
    if (
      prePopulateData &&
      prePopulateData.venueId &&
      !editingEvent &&
      !isFormInitialized
    ) {
      setFormData((prev) => ({
        ...prev,
        venue: prePopulateData.venueId,
        city: prePopulateData.city || "",
      }));

      // Clear pre-populate data after use
      setTimeout(() => {
        setPrePopulateEventData({ venueId: null, city: null });
      }, 0);
    }
  }, [
    prePopulateData,
    editingEvent,
    isFormInitialized,
    setPrePopulateEventData,
  ]);

  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target;

      setFormData((prev) => {
        let newFormData = { ...prev };

        if (name === "venue") {
          const selectedVenue = venues.find((venue) => venue._id === value);
          newFormData = {
            ...prev,
            venue: value,
            city: selectedVenue?.city || "",
            includedInPrice: selectedVenue?.includedInPrice || "",
            optionalExtras: selectedVenue?.optionalExtras || "",
          };
        } else if (name === "basePrice") {
          newFormData = {
            ...prev,
            [name]: parseFloat(value),
          };
        } else {
          newFormData = {
            ...prev,
            [name]: value,
          };
        }

        // Sync with context - only if not in edit mode
        if (!editingEvent) {
          setEventFormData(newFormData);
        }

        return newFormData;
      });

      // Clear any related errors
      if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: "" }));
      }
    },
    [venues, editingEvent, setEventFormData, errors]
  );

  const handleFileChange = useCallback(
    (e) => {
      const file = e.target.files[0];

      if (!file) return;
      setOriginalFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImageSrc(e.target.result);
        setIsCropOpen(true);
      };
      reader.readAsDataURL(file);

    },
    [editingEvent, eventFormData]
  );

  const validateForm = useCallback(() => {
    const newErrors = {};

    // Required field validations
    if (!formData.title?.trim()) {
      newErrors.title = "Title is required";
    }

    if (!formData.venue) {
      newErrors.venue = "Venue is required";
    }

    if (
      !Array.isArray(formData.date_times) ||
      formData.date_times.length === 0
    ) {
      newErrors.date_times = "At least one date is required";
    }
    // Optional but recommended validations
    if (!formData.genre?.trim()) {
      newErrors.genre = "Genre is recommended";
    }

    if (!Array.isArray(formData.composers) || formData.composers.length === 0) {
      newErrors.composers = "At least one composer is recommended";
    }

    setErrors((prev) => ({ ...prev, ...newErrors }));
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  // Get venue's default hotel info if available
  const selectedVenueDefaultHotel = useMemo(() => {
    if (!formData.venue) return null;

    // First try to get from our mapping
    const defaultHotel = venueDefaultHotels[formData.venue];
    if (defaultHotel) {
      //console.log('Found default hotel in venueDefaultHotels map:', defaultHotel);
      // Add hotel name if available
      if (hotelNames[defaultHotel.hotelId]) {
        return {
          ...defaultHotel,
          hotelName: hotelNames[defaultHotel.hotelId],
        };
      }
      return defaultHotel;
    }

    // If not found in our mapping, try to get directly from the venues list
    const selectedVenue = venues.find((v) => v._id === formData.venue);
    if (selectedVenue?.defaultHotelRoom?.hotelId) {
      //console.log('Found default hotel directly in venue object:', selectedVenue.defaultHotelRoom);
      // Add hotel name if available
      if (hotelNames[selectedVenue.defaultHotelRoom.hotelId]) {
        return {
          ...selectedVenue.defaultHotelRoom,
          hotelName: hotelNames[selectedVenue.defaultHotelRoom.hotelId],
        };
      }
      return selectedVenue.defaultHotelRoom;
    }

    //console.log('No default hotel found for venue:', formData.venue);
    return null;
  }, [formData.venue, venueDefaultHotels, venues, hotelNames]);

  const handleDateChange = useCallback(
    (dates) => {
      if (!Array.isArray(dates)) return;

      const updatedDateTimes = dates.map((date) => ({
        date: new Date(date),
        time: formData.defaultTime || "19:00",
        cost: "0",
      }));

      setFormData((prev) => ({
        ...prev,
        date_times: updatedDateTimes,
      }));

      // Sync with context - only if not in edit mode
      if (!editingEvent) {
        setEventFormData((prev) => ({
          ...prev,
          date_times: updatedDateTimes,
        }));
      }

      // Clear date-related errors
      if (errors.date_times || errors.dateTimes) {
        setErrors((prev) => ({
          ...prev,
          date_times: "",
          dateTime: "",
        }));
      }
    },
    [formData.defaultTime, editingEvent, setEventFormData, errors]
  );

  const handleDefaultTimeChange = useCallback(
    (newTime) => {
      // if (!newTime) return;

      setFormData((prev) => {
        const updatedFormData = {
          ...prev,
          defaultTime: newTime,
          date_times: prev.date_times.map((dt) => ({
            ...dt,
            time: newTime,
          })),
        };

        // Sync with context - only if not in edit mode
        if (!editingEvent) {
          setEventFormData(updatedFormData);
        }

        return updatedFormData;
      });
    },
    [editingEvent, setEventFormData]
  );

  const handleIndividualTimeChange = useCallback(
    (index, newTime) => {
      // if (!newTime || index < 0) return;

      setFormData((prev) => {
        const updatedFormData = {
          ...prev,
          date_times: prev.date_times.map((dt, i) =>
            i === index ? { ...dt, time: newTime } : dt
          ),
        };
        // Sync with context - only if not in edit mode
        if (!editingEvent) {
          setEventFormData(updatedFormData);
        }

        return updatedFormData;
      });
    },
    [editingEvent, setEventFormData]
  );
  const handleIndividualCostChange = useCallback(
    (index, newCost) => {
      if (!newCost || index < 0) return;

      setFormData((prev) => {
        const updatedFormData = {
          ...prev,
          date_times: prev.date_times.map((dt, i) =>
            i === index ? { ...dt, cost: parseFloat(newCost) } : dt
          ),
        };

        // Sync with context - only if not in edit mode
        if (!editingEvent) {
          setEventFormData(updatedFormData);
        }

        return updatedFormData;
      });
    },
    [editingEvent, setEventFormData]
  );
  const handleNonFamousPerformerChange = useCallback((e) => {
    const { name, value } = e.target;
    setNonFamousPerformer((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, performer: "" }));
  }, []);

  const handleFamousPerformerInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFamousPerformerInput((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, performer: "" }));
  }, []);

  const handleNewCrewMemberChange = useCallback((e) => {
    const { name, value } = e.target;
    setNewCrewMember((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, crew: "" }));
  }, []);

  const isPerformerInCast = useCallback(
    (name) => {
      return formData.cast.some(
        (performer) => performer.name.toLowerCase() === name.toLowerCase()
      );
    },
    [formData.cast]
  );

  const updateCastInBothStates = useCallback(
    (newCast) => {
      setFormData((prev) => ({ ...prev, cast: newCast }));

      // Sync with context - only if not in edit mode
      if (!editingEvent) {
        setEventFormData((prev) => ({ ...prev, cast: newCast }));
      }
    },
    [editingEvent, setEventFormData]
  );

  const addNonFamousPerformer = useCallback(() => {
    if (!nonFamousPerformer.name?.trim() || !nonFamousPerformer.role?.trim()) {
      setErrors((prev) => ({
        ...prev,
        performer: "Name and role are required",
      }));
      return;
    }

    if (isPerformerInCast(nonFamousPerformer.name)) {
      setErrors((prev) => ({
        ...prev,
        performer: "Performer already exists in the cast",
      }));
      return;
    }

    const newCast = [...formData.cast, { ...nonFamousPerformer }];
    updateCastInBothStates(newCast);
    setNonFamousPerformer({ name: "", role: "" });
    setErrors((prev) => ({ ...prev, performer: "" }));
  }, [
    nonFamousPerformer,
    formData.cast,
    updateCastInBothStates,
    isPerformerInCast,
  ]);

  const editNonFamousPerformer = useCallback(
    (index) => {
      if (index < 0 || index >= formData.cast.length) return;

      const performer = formData.cast[index];
      setNonFamousPerformer({
        name: performer.name || "",
        role: performer.role || "",
      });

      const newCast = formData.cast.filter((_, i) => i !== index);
      updateCastInBothStates(newCast);
      setErrors((prev) => ({ ...prev, performer: "" }));
    },
    [formData.cast, updateCastInBothStates]
  );

  const deleteNonFamousPerformer = useCallback(
    (index) => {
      if (index < 0 || index >= formData.cast.length) return;

      const newCast = formData.cast.filter((_, i) => i !== index);
      updateCastInBothStates(newCast);
      setErrors((prev) => ({ ...prev, performer: "" }));
    },
    [formData.cast, updateCastInBothStates]
  );

  const addToFamousPerformers = async (performer) => {
    try {
      await addFamousPerformer(performer);
      //console.log('Performer added to famous performers');
      setErrors((prev) => ({
        ...prev,
        performerSuccess: `${performer.name} added to Famous Performers`,
      }));
    } catch (err) {
      setErrors((prev) => ({ ...prev, performer: err.message }));
    }
  };

  const addFamousPerformerToEvent = useCallback(() => {
    if (!famousPerformerInput.id || !famousPerformerInput.role?.trim()) {
      setErrors((prev) => ({
        ...prev,
        performer: "Famous performer and role are required",
      }));
      return;
    }

    const performer = allFamousPerformers.find(
      (p) => p._id === famousPerformerInput.id
    );

    if (!performer) {
      setErrors((prev) => ({
        ...prev,
        performer: "Selected performer not found",
      }));
      return;
    }

    if (isPerformerInCast(performer.name)) {
      setErrors((prev) => ({
        ...prev,
        performer: "Performer already exists in the cast",
      }));
      return;
    }

    const newCastMember = {
      name: performer.name,
      role: famousPerformerInput.role.trim(),
      famousPerformer: performer._id,
    };

    const newCast = [...formData.cast, newCastMember];
    updateCastInBothStates(newCast);
    setFamousPerformerInput({ id: "", role: "" });
    setErrors((prev) => ({ ...prev, performer: "" }));
  }, [
    famousPerformerInput,
    allFamousPerformers,
    formData.cast,
    updateCastInBothStates,
    isPerformerInCast,
  ]);

  const isComposerInEvent = useCallback(
    (composerId, composerName) => {
      if (composerId) {
        return formData.composers.includes(composerId);
      }
      if (composerName) {
        return formData.composers.some((id) => {
          const composer = allComposers.find((c) => c._id === id);
          return (
            composer &&
            composer.name.toLowerCase() === composerName.toLowerCase()
          );
        });
      }
      return false;
    },
    [formData.composers, allComposers]
  );

  const updateComposersInBothStates = useCallback(
    (newComposers) => {
      setFormData((prev) => ({ ...prev, composers: newComposers }));

      // Sync with context - only if not in edit mode
      if (!editingEvent) {
        setEventFormData((prev) => ({ ...prev, composers: newComposers }));
      }
    },
    [editingEvent, setEventFormData]
  );

  const addComposerToEvent = useCallback(() => {
    if (!composerId) {
      setErrors((prev) => ({
        ...prev,
        composer: "Please select a composer",
      }));
      return;
    }

    if (isComposerInEvent(composerId)) {
      setErrors((prev) => ({
        ...prev,
        composer: "Composer already added to the event",
      }));
      return;
    }

    const composer = allComposers.find((c) => c._id === composerId);
    if (!composer) {
      setErrors((prev) => ({
        ...prev,
        composer: "Selected composer not found",
      }));
      return;
    }

    const newComposers = [...formData.composers, composer._id];
    updateComposersInBothStates(newComposers);
    setComposerId("");
    setErrors((prev) => ({ ...prev, composer: "" }));
  }, [
    composerId,
    formData.composers,
    allComposers,
    updateComposersInBothStates,
    isComposerInEvent,
  ]);

  const addNewComposer = useCallback(async () => {
    if (!newComposerName?.trim()) {
      setErrors((prev) => ({
        ...prev,
        newComposer: "Composer name is required",
      }));
      return;
    }

    if (isComposerInEvent(null, newComposerName)) {
      setErrors((prev) => ({
        ...prev,
        newComposer: "Composer already added to the event",
      }));
      return;
    }

    try {
      const composer = await addComposer(newComposerName.trim());
      const newComposers = [...formData.composers, composer._id];
      updateComposersInBothStates(newComposers);
      setNewComposerName("");
      setErrors((prev) => ({ ...prev, newComposer: "" }));
    } catch (err) {
      setErrors((prev) => ({ ...prev, newComposer: err.message }));
    }
  }, [
    newComposerName,
    formData.composers,
    updateComposersInBothStates,
    isComposerInEvent,
    addComposer,
  ]);

  const removeComposer = useCallback(
    (composerId) => {
      const newComposers = formData.composers.filter((id) => id !== composerId);
      updateComposersInBothStates(newComposers);
      setErrors((prev) => ({ ...prev, composer: "", newComposer: "" }));
    },
    [formData.composers, updateComposersInBothStates]
  );

  const isCrewMemberInList = useCallback(
    (name) => {
      return formData.crews.some(
        (crew) => crew.name.toLowerCase() === name.toLowerCase()
      );
    },
    [formData.crews]
  );

  const updateCrewsInBothStates = useCallback(
    (newCrews) => {
      setFormData((prev) => ({ ...prev, crews: newCrews }));

      // Sync with context - only if not in edit mode
      if (!editingEvent) {
        setEventFormData((prev) => ({ ...prev, crews: newCrews }));
      }
    },
    [editingEvent, setEventFormData]
  );

  const addCrewMember = useCallback(() => {
    // if (!newCrewMember.name?.trim() || !newCrewMember.role?.trim()) {
    //   setErrors((prev) => ({
    //     ...prev,
    //     crew: "Name and role are required",
    //   }));
    //   return;
    // }

    if (isCrewMemberInList(newCrewMember.name)) {
      setErrors((prev) => ({
        ...prev,
        crew: "Crew member already exists",
      }));
      return;
    }

    const newCrew = {
      name: newCrewMember.name.trim(),
      role: newCrewMember.role.trim(),
    };

    const newCrews = [...formData.crews, newCrew];
    updateCrewsInBothStates(newCrews);
    setNewCrewMember({ name: "", role: "" });
    setErrors((prev) => ({ ...prev, crew: "" }));
  }, [
    newCrewMember,
    formData.crews,
    updateCrewsInBothStates,
    isCrewMemberInList,
  ]);

  const editCrewMember = useCallback(
    (index) => {
      if (index < 0 || index >= formData.crews.length) return;

      const crewMember = formData.crews[index];
      setNewCrewMember({
        name: crewMember.name || "",
        role: crewMember.role || "",
      });

      const newCrews = formData.crews.filter((_, i) => i !== index);
      updateCrewsInBothStates(newCrews);
      setErrors((prev) => ({ ...prev, crew: "" }));
    },
    [formData.crews, updateCrewsInBothStates]
  );

  const deleteCrewMember = useCallback(
    (index) => {
      if (index < 0 || index >= formData.crews.length) return;

      const newCrews = formData.crews.filter((_, i) => i !== index);
      updateCrewsInBothStates(newCrews);
      setErrors((prev) => ({ ...prev, crew: "" }));
    },
    [formData.crews, updateCrewsInBothStates]
  );

  const resetForm = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setIsSectionOpen(false);
    setFormData(initialFormData);
    setEventFormData(initialFormData);
    setNonFamousPerformer({ name: "", role: "" });
    setFamousPerformerInput({ id: "", role: "" });
    setNewCrewMember({ name: "", role: "" });
    setComposerId("");
    setNewComposerName("");
    setIsFormInitialized(false);

    // Clear date picker
    if (flatpickrRef.current?.flatpickr) {
      flatpickrRef.current.flatpickr.clear();
    }

    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    setErrors({});
  }, [initialFormData, setEventFormData]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      if (!validateForm()) {
        return;
      }

      if (
        !Array.isArray(formData.date_times) ||
        formData.date_times.length === 0
      ) {
        setErrors((prev) => ({
          ...prev,
          dateTime: "Please select at least one date",
        }));
        return;
      }

      try {
        const data = new FormData();
        data.append("title", formData.title.trim());
        data.append("venue", formData.venue);
        data.append("city", formData.city || "");
        data.append("genre", formData.genre || "");
        data.append("includedInPrice", formData.includedInPrice || "");
        data.append("optionalExtras", formData.optionalExtras || "");
        data.append("composers", JSON.stringify(formData.composers));
        data.append("cast", JSON.stringify(formData.cast));
        data.append("crews", JSON.stringify(formData.crews));
        data.append("ensamble", JSON.stringify({ Orchester: "", Chor: "" }));

        const formattedDateTimes = formData.date_times.map((dt) => {
          try {
            const inputDate = new Date(dt.date);

            if (dt.time) {
              // time provided → full ISO timestamp
              const [hStr, mStr] = dt.time.split(":");
              const hours = parseInt(hStr, 10) || 0;
              const minutes = parseInt(mStr, 10) || 0;

              const localDate = new Date(
                inputDate.getFullYear(),
                inputDate.getMonth(),
                inputDate.getDate(),
                hours,
                minutes,
                0,
                0
              );

              return {
                date: localDate.toISOString(), // full timestamp
                time: dt.time,
                cost: dt.cost ?? "0",
              };
            } else {
              // no time → save only YYYY-MM-DD string
              const yyyy = inputDate.getFullYear();
              const mm = String(inputDate.getMonth() + 1).padStart(2, "0");
              const dd = String(inputDate.getDate()).padStart(2, "0");

              return {
                date: `${yyyy}-${mm}-${dd}`, // 👈 plain date string, no T00
                time: null,
                cost: dt.cost ?? "0",
              };
            }
          } catch (error) {
            console.error("Error formatting date/time:", error, dt);
            return { date: new Date().toISOString(), time: null, cost: "0" };
          }
        });

        data.append("date_times", JSON.stringify(formattedDateTimes));
        // Ensure basePrice is valid
        const basePrice = parseFloat(formData.basePrice);
        data.append("basePrice", isNaN(basePrice) ? "0" : basePrice.toString());

        // Only append image if it's a valid file
        if (formData?.image instanceof File) {
          data.append("image", formData?.image);
        }
        const formDataObj = {};
        for (let pair of data.entries()) {
          formDataObj[pair[0]] = pair[1];
        }

        const res = await submitEvent(data, !!editingEvent, editingEvent?._id);
        const resPublishEvent = publishEvents
          ? await handlePublish(editingEvent?._id)
          : null;

        if ((res.status === 201 || res.status === 200) && !publishEvents) {
          setEditingEvent(null);
          setEventFormData(null);
          resetForm();
          toast.success(
            `Event ${editingEvent ? "updated" : "created"} successfully!`
          );
        }
        if (
          publishEvents &&
          res.status === 200 &&
          resPublishEvent?.status === 200
        ) {
          setEditingEvent(null);
          setEventFormData(null);
          resetForm();
          toast.success(`Event updated and published successfully!`);
          fetchAdminData();
        }
      } catch (err) { console.error(err) }
    },
    [
      formData,
      editingEvent,
      validateForm,
      submitEvent,
      setEditingEvent,
      setEventFormData,
      resetForm,
    ]
  );
  const handleOpenPicker = useGoogleDriveImagePicker({
    isEditing: editingEvent ? true : false,
    setFormData: setEventFormData,
    setFormDataEdit: setEditingEvent,
    setError: setErrors,
  });

  return (
    <div className="max-w-[1380px] mx-auto bg-[#1a1a1a] rounded-lg text-white">
      <ImageModal
        open={modalOpen}
        image={modalImage}
        onClose={() => setModalOpen(false)}
      />
      <Collapsible
        title={editingEvent ? "Edit Event" : "Add Event"}
        defaultOpen={true}
        isOpen={isSectionOpen}
        setIsOpen={setIsSectionOpen}
      >
        <div>
          {errors.form && (
            <p className="text-[#f44336] text-center mb-4">{errors.form}</p>
          )}
          {errors.success && (
            <p className="text-[#4CAF50] text-center mb-4">{errors.success}</p>
          )}
          {errors.performerSuccess && (
            <p className="text-[#4CAF50] text-center mb-4">
              {errors.performerSuccess}
            </p>
          )}
          <form
            onSubmit={handleSubmit}
            className="flex flex-col gap-4 w-full mb-8"
          >
            <div className="flex flex-col gap-1">
              <label className="text-white">Event Title *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                className={`${inputClass} focus:border-[#d4a017]`}
              />
              {errors.title && (
                <span className="text-[#f44336] text-sm text-center">
                  {errors.title}
                </span>
              )}
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-white">Venue *</label>
              <select
                name="venue"
                value={formData.venue || ""}
                onChange={handleChange}
                required
                className={`${inputClass} focus:border-[#d4a017]`}
              >
                <option value="">Select Venue</option>
                {venues.map((venue) => (
                  <option key={venue._id} value={venue._id}>
                    {venue.name}
                  </option>
                ))}
              </select>
              {errors.venue && (
                <span className="text-[#f44336] text-sm text-center">
                  {errors.venue}
                </span>
              )}
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-white">City</label>
              <input
                type="text"
                name="city"
                value={
                  formData.city ||
                  venues.find((v) => v._id === formData.venue)?.city ||
                  ""
                }
                onChange={handleChange}
                readOnly
                className={`${inputClass}`}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-white">Base Price (€) *</label>
              <input
                type="number"
                name="basePrice"
                value={formData.basePrice}
                onChange={handleChange}
                min={0}
                required
                className={`${inputClass}`}
                onWheel={(e) => e.target.blur()}
              />
              <p className="text-[#ccc] text-sm">
                Price for 2 nights + ticket for 1 person, using venue's default
                hotel.
              </p>
              {errors.basePrice && (
                <span className="text-[#f44336] text-sm">
                  {errors.basePrice}
                </span>
              )}
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-white">Included in Price</label>
              <textarea
                name="includedInPrice"
                value={formData.includedInPrice}
                onChange={handleChange}
                className="w-full p-2 text-base bg-[#333] text-white border border-[#555] rounded-md min-h-[100px] resize-y"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-white">Optional Extras</label>
              <textarea
                name="optionalExtras"
                value={formData.optionalExtras}
                onChange={handleChange}
                className="w-full p-2 text-base bg-[#333] text-white border border-[#555] rounded-md min-h-[100px] resize-y"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-white">Genre</label>
              <input
                type="text"
                name="genre"
                value={formData.genre}
                onChange={handleChange}
                className={`${inputClass}`}
              />
              {errors.genre && (
                <span className="text-[#ffa726] text-sm text-center">
                  {errors.genre}
                </span>
              )}
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-white">Select Dates *</label>
              <Flatpickr
                ref={flatpickrRef}
                value={formData.date_times.map((dt) => dt.date)}
                onChange={handleDateChange}
                clearIcon={null}
                options={{ mode: "multiple", dateFormat: "Y-m-d" }}
                className={`${inputClass} focus:border-[#d4a017]`}
              />
              {errors.dateTime && (
                <span className="text-[#f44336] text-sm text-center">
                  {errors.dateTime}
                </span>
              )}
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-white">Default Time</label>
              <TimePicker
                value={formData.defaultTime}
                onChange={handleDefaultTimeChange}
                format="HH:mm"
                disableClock
                minTime="00:00"
                maxTime="23:59"
                className={`${inputClass} w-fit`}
              />
            </div>
            {formData.date_times.length > 0 && (
              <div className="flex flex-wrap gap-5">
                {formData.date_times.map((dt, index) => (
                  <div key={index} className="flex flex-col gap-2">
                    <span className="text-white text-center md:text-left">
                      {new Date(dt.date).toISOString().split("T")[0]}
                    </span>
                    <TimePicker
                      value={dt.time || null}
                      onChange={(newTime) =>
                        handleIndividualTimeChange(index, newTime)
                      }
                      format="HH:mm"
                      disableClock
                      minTime="00:00"
                      maxTime="23:59"
                      className={`${inputClass} w-fit focus:border-[#d4a017]`}
                    />
                    <div className="w-28">
                      <label>Additional cost</label>
                      <input
                        type="number"
                        value={dt.cost}
                        onChange={(e) =>
                          handleIndividualCostChange(index, e.target.value)
                        }
                        className={`${inputClass}`}
                        placeholder="Additional cost"
                        onWheel={(e) => e.target.blur()}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex flex-col gap-2">
              <label className="text-white">Select Composer</label>
              <select
                value={composerId}
                onChange={(e) => setComposerId(e.target.value)}
                className={`${inputClass} focus:border-[#d4a017]`}
              >
                <option value="">Select Composer</option>
                {allComposers.map((composer) => (
                  <option key={composer._id} value={composer._id}>
                    {composer.name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={addComposerToEvent}
                className="w-full md:w-auto px-4 py-2 bg-[#d4a017] hover:bg-[#b38b00] text-white rounded-md transition-colors mx-auto md:mx-0"
              >
                Add Composer
              </button>
              {errors.composer && (
                <span className="text-[#f44336] text-sm text-center">
                  {errors.composer}
                </span>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-white">Add New Composer</label>
              <input
                type="text"
                value={newComposerName}
                onChange={(e) => setNewComposerName(e.target.value)}
                className={`${inputClass}`}
              />
              <button
                type="button"
                onClick={addNewComposer}
                className="w-full md:w-auto px-4 py-2 bg-[#d4a017] hover:bg-[#b38b00] text-white rounded-md transition-colors mx-auto md:mx-0"
              >
                Add New Composer
              </button>
              {errors.newComposer && (
                <span className="text-[#f44336] text-sm text-center">
                  {errors.newComposer}
                </span>
              )}
            </div>
            {formData.composers.length > 0 && (
              <ul className="list-none p-0 mt-2">
                {formData.composers.map((id) => {
                  const composer = allComposers.find((c) => c._id === id);
                  return composer ? (
                    <li
                      key={id}
                      className="flex flex-col md:flex-row justify-between items-center py-1"
                    >
                      <span className="text-white text-center md:text-left">
                        {composer.name}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeComposer(id)}
                        className="px-3 py-1 bg-[#f44336] hover:bg-[#da190b] text-white text-sm rounded-md transition-colors mt-1 md:mt-0"
                      >
                        Remove
                      </button>
                    </li>
                  ) : null;
                })}
              </ul>
            )}
            {errors.composers && (
              <span className="text-[#ffa726] text-sm text-center">
                {errors.composers}
              </span>
            )}

            <div className="flex flex-col gap-2">
              <label className="text-white">Add New Performer</label>
              <input
                type="text"
                name="name"
                value={nonFamousPerformer.name}
                onChange={handleNonFamousPerformerChange}
                placeholder="Performer Name"
                className={`${inputClass}`}
              />
              <input
                type="text"
                name="role"
                value={nonFamousPerformer.role}
                onChange={handleNonFamousPerformerChange}
                placeholder="Performer Role"
                className={`${inputClass}`}
              />
              <button
                type="button"
                onClick={addNonFamousPerformer}
                className="w-full md:w-auto px-4 py-2 bg-[#d4a017] hover:bg-[#b38b00] text-white rounded-md transition-colors mx-auto md:mx-0"
              >
                Add Performer
              </button>
              {errors.performer && (
                <span className="text-[#f44336] text-sm text-center">
                  {errors.performer}
                </span>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-white">Add Famous Artist</label>
              <select
                name="id"
                value={famousPerformerInput.id}
                onChange={handleFamousPerformerInputChange}
                className={`${inputClass} focus:border-[#d4a017]`}
              >
                <option value="">Select Artist</option>
                {allFamousPerformers.map((performer) => (
                  <option key={performer._id} value={performer._id}>
                    {performer.name}
                  </option>
                ))}
              </select>
              <input
                type="text"
                name="role"
                value={famousPerformerInput.role}
                onChange={handleFamousPerformerInputChange}
                placeholder="Artist Role"
                className={`${inputClass}`}
              />
              <button
                type="button"
                onClick={addFamousPerformerToEvent}
                className="w-full md:w-auto px-4 py-2 bg-[#d4a017] hover:bg-[#b38b00] text-white rounded-md transition-colors mx-auto md:mx-0"
              >
                Add Artist
              </button>
            </div>
            {formData.cast.length > 0 && (
              <CastTable
                cast={formData.cast}
                editNonFamousPerformer={editNonFamousPerformer}
                deleteNonFamousPerformer={deleteNonFamousPerformer}
                addToFamousPerformers={addToFamousPerformers}
              />
            )}

            <div className="flex flex-col gap-2">
              <label className="text-white">Add Crew Member</label>
              <input
                type="text"
                name="name"
                value={newCrewMember.name}
                onChange={handleNewCrewMemberChange}
                placeholder="Crew Member Name"
                className={`${inputClass}`}
              />
              <input
                type="text"
                name="role"
                value={newCrewMember.role}
                onChange={handleNewCrewMemberChange}
                placeholder="Crew Member Role"
                className={`${inputClass}`}
              />
              <button
                type="button"
                onClick={addCrewMember}
                className="w-full md:w-auto px-4 py-2 bg-[#d4a017] hover:bg-[#b38b00] text-white rounded-md transition-colors mx-auto md:mx-0"
              >
                Add Crew Member
              </button>
              {errors.crew && (
                <span className="text-[#f44336] text-sm text-center">
                  {errors.crew}
                </span>
              )}
            </div>
            {formData.crews.length > 0 && (
              <CrewTable
                crews={formData.crews}
                editCrewMember={editCrewMember}
                deleteCrewMember={deleteCrewMember}
              />
            )}
            <ImageCropModal
              imageSrc={imageSrc}
              originalFileName={originalFile?.name || 'Event'}
              isOpen={isCropOpen}
              onClose={() => setIsCropOpen(false)}
              onSave={(croppedFile) => {
                setFormData((prev) => ({ ...prev, image: croppedFile }));
                if (!editingEvent) {
                  setEventFormData((prev) => ({ ...prev, image: croppedFile }));
                }
              }}
            />
            {(formData?.image || eventFormData?.image) && (
              <ImagePreview
                image={formData?.image || eventFormData?.image}
                onClick={openModal}
              />
            )}

            <div className="flex flex-col gap-2">
              <label className="text-white">Event Image</label>
              <div className="flex gap-2">
                <div className="relative">
                  <input
                    type="file"
                    id="event-image-file"
                    name="image"
                    onChange={handleFileChange}
                    ref={fileInputRef}
                    className={`${inputClass} !w-fit pl-10 pr-3 py-2`}
                  />
                  <label
                    htmlFor="event-image-file"
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 cursor-pointer pointer-events-none"
                  >
                    <img
                      src="/images/attach.svg"
                      alt="Choose File"
                      className="w-4 h-4 filter invert"
                    />
                  </label>
                </div>
                <button
                  type="button"
                  onClick={() => handleOpenPicker("image")}
                  className="px-3 py-2 bg-[#666] text-white rounded-md hover:bg-[#555] transition-colors w-fit flex items-center gap-2"
                >
                  <img
                    src="/images/drive.png"
                    alt="Google Drive"
                    className="w-4 h-4"
                  />
                  Google Drive
                </button>
              </div>
            </div>
            {formData.venue && selectedVenueDefaultHotel && (
              <p className="text-[#d4a017] text-sm mt-1">
                Default Hotel:{" "}
                {venues.find((v) => v._id === formData.venue)?.name} uses{" "}
                {selectedVenueDefaultHotel.hotelName ||
                  hotelNames[selectedVenueDefaultHotel.hotelId] ||
                  `Hotel ID: ${selectedVenueDefaultHotel.hotelId}`}{" "}
                - {selectedVenueDefaultHotel.roomType}
              </p>
            )}
            {formData.venue && !selectedVenueDefaultHotel && (
              <p className="text-[#f44336] text-sm mt-1">
                Warning: Selected venue does not have a default hotel. Please
                set one in the Venue tab first.
              </p>
            )}
            <div className="flex flex-col md:flex-row gap-2">
              <button
                type="submit"
                className="w-full md:w-auto px-4 py-2 bg-[#d4a017] hover:bg-[#b38b00] text-white rounded-md transition-colors mx-auto md:mx-0"
              >
                {editingEvent ? "Update Event" : "Add Event"}
              </button>
              {editingEvent && !editingEvent.published && (
                <button
                  type="submit"
                  className="w-full md:w-auto px-4 py-2 bg-[#d4a017] hover:bg-[#b38b00] text-white rounded-md transition-colors mx-auto md:mx-0"
                  onClick={() => setPublishEvents(true)}
                >
                  Update & Publish Event
                </button>
              )}
              {editingEvent && (
                <button
                  type="button"
                  onClick={() => {
                    setEditingEvent(null);
                    resetForm();
                  }}
                  className="w-full md:w-auto px-4 py-2 bg-[#333] hover:bg-[#555] text-white rounded-md transition-colors mx-auto md:mx-0"
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </div>
      </Collapsible>
    </div>
  );
}

export default EventForm;
